<template>
  <div class="list-container">
    <div class="top-fix-area" ref="topFixArea">
      <h1 class="page-title">測定種別情報一覧</h1>
      <div class="mb-2">
        <router-link v-if="isAdmin" :to="{ path: '/create-measure-type', query: getSearchQuery() }" class="btn btn-sm btn-primary me-2">
          測定種別情報作成
        </router-link>

        <button v-if="isAdmin" type="button" class="btn btn-sm btn-primary me-2" data-bs-toggle="modal" data-bs-target="#exampleModal">
          CSVインポート
        </button>

        <button type="button" class="btn btn-sm btn-primary me-2" @click="downloadCsv">
          CSVエクスポート
        </button>

        <button class="btn btn-sm btn-outline-secondary" @click="isSearchExpanded = !isSearchExpanded">
          {{ isSearchExpanded ? '検索条件を非表示' : '検索条件を表示' }}
        </button>
      </div>

      <div class="modal fade" id="exampleModal" data-bs-backdrop="static" data-bs-keyboard="false" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-lg">
          <div class="modal-content">
            <div class="modal-header">
              <h1 class="modal-title fs-5">CSVインポート</h1>
              <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close" @click="clearErrorMessages"></button>
            </div>
            <div class="modal-body">
              <div v-if="errorMessages">
                <div class="error-massage-item" v-for="errorMessage in errorMessages" :key="errorMessage.id">
                  <p class="alert alert-danger mt-2">{{ errorMessage.row_num }}行目：{{ errorMessage.messages[0] }}</p>
                </div>
              </div>

              <div class="my-3">
                <p>測定種別情報CSVファイル選択</p>
                <input id="measureTypeCsvFileInput" type="file" class="form-control" @change="handleFileChange">
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" @click="clearErrorMessages">閉じる</button>
              <button type="button" class="btn btn-primary" @click="submitFile">インポート</button>
            </div>
          </div>
        </div>
      </div>

      <SearchPanelWrapper :key="route.fullPath" v-model="isSearchExpanded" :getTableElement="() => tableWrapper?.$el || tableWrapper?.value">
        <form class="search-container" @submit.prevent="searchMeasureTypes">
          <div class="row g-3 align-items-center">
            <div class="col-auto">
              <label for="searchName" class="col-form-label">測定種別名</label>
            </div>
            <div class="col-auto">
              <input id="searchName" v-model="searchName" type="text" class="form-control" placeholder="測定種別名で検索">
            </div>

            <DeletedStatusSelect v-model="searchDeletedStatus" id="measureTypeDeletedStatus" />

            <div class="col-auto">
              <button type="submit" class="btn btn-sm btn-primary mx-1">検索</button>
              <button type="button" class="btn btn-sm btn-secondary mx-1" @click="searchResetMeasureTypes">クリア</button>
            </div>
          </div>
        </form>
      </SearchPanelWrapper>
    </div>

    <div class="d-flex justify-content-end" ref="paginationContainer">
      <Pagination
        :currentPage="currentPage"
        :totalPages="totalPages"
        @change-page="handleChangePage"
      />
    </div>

    <div class="table-wrapper" ref="tableWrapper">
      <table class="master-table table table-bordered">
        <thead class="table-dark">
          <tr>
            <th class="text-nowrap">ID</th>
            <th class="text-nowrap">測定種別</th>
            <th class="btn-cell"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="measure_type in measure_types" :key="measure_type.id" :class="{ 'no-active-measure-type': measure_type.del_flg == 1 }">
            <td>{{ measure_type.id }}</td>
            <td>{{ measure_type.measure_type_nm }}</td>
            <td class="btn-cell text-nowrap">
              <router-link :to="{ name: 'MeasureTypeDetail', params: { id: measure_type.id }, query: getSearchQuery() }">
                <button type="button" class="btn btn-sm btn-info">詳細</button>
              </router-link>

              <router-link v-if="isAdmin" :to="{ name: 'MeasureTypeEdit', params: { id: measure_type.id }, query: getSearchQuery() }">
                <button type="button" class="btn btn-sm btn-success mx-2">編集</button>
              </router-link>

              <button
                v-if="measure_type.del_flg == 0 && isAdmin"
                class="btn btn-sm btn-danger"
                data-bs-toggle="modal"
                data-bs-target="#deleteModal"
                @click="handleDeleteClick(measure_type.id)"
              >
                削除
              </button>
              <button
                v-if="measure_type.del_flg == 1 && isAdmin"
                class="btn btn-sm btn-warning"
                data-bs-toggle="modal"
                data-bs-target="#restoreModal"
                @click="handleRestoreClick(measure_type.id)"
              >
                復元
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <DeleteConfirmationModal
      :paramsId="paramsIdToDelete"
      @confirmDelete="handleConfirmDelete"
    />

    <RestoreConfirmationModal
      :paramsId="paramsIdToRestore"
      @confirmRestore="handleConfirmRestore"
    />
  </div>
</template>

<script>
import { ref, onMounted, watchEffect, computed } from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import {
  getMeasureTypes,
  deleteMeasureType,
  restoreMeasureType,
  exportMeasureTypesCsv,
  importMeasureTypesCsv
} from '@/api/axios';
import { Modal } from 'bootstrap';
import SearchPanelWrapper from '@/components/searchPanels/SearchPanelWrapper.vue';
import DeleteConfirmationModal from '@/components/shared/DeleteConfirmationModal.vue';
import RestoreConfirmationModal from '@/components/shared/RestoreConfirmationModal.vue';
import Pagination from '@/components/shared/Pagination.vue';
import DeletedStatusSelect from '@/components/shared/DeletedStatusSelect.vue';
import { currentRoleFromStoreOrLocalStorage, isAdminRole } from '@/utils/roleAuthorization';

const PER_PAGE = 50;

export default {
  components: {
    SearchPanelWrapper,
    DeleteConfirmationModal,
    RestoreConfirmationModal,
    Pagination,
    DeletedStatusSelect
  },
  setup() {
    const title = ref('測定種別情報一覧');

    watchEffect(() => {
      document.title = title.value;
    });

    const store = useStore();
    const isAdmin = computed(() => isAdminRole(currentRoleFromStoreOrLocalStorage(store.state.userRole)));
    const router = useRouter();
    const route = useRoute();

    const allMeasureTypes = ref([]);
    const measure_types = ref([]);
    const selectedFile = ref(null);
    const errorMessages = ref({});

    const paramsIdToDelete = ref(null);
    const paramsIdToRestore = ref(null);

    const searchName = ref('');
    const searchDeletedStatus = ref('active');

    const currentPage = ref(1);
    const totalPages = ref(1);

    const isSearchExpanded = ref(true);
    const topFixArea = ref(null);
    const tableWrapper = ref(null);
    const paginationContainer = ref(null);

    const getSearchQuery = () => ({
      measure_type_name: searchName.value,
      measure_type_deleted_status: searchDeletedStatus.value,
      measure_type_page: currentPage.value || 1
    });

    const applyPagination = (page = 1) => {
      totalPages.value = Math.max(Math.ceil(allMeasureTypes.value.length / PER_PAGE), 1);
      const safePage = Math.min(Math.max(page, 1), totalPages.value);
      currentPage.value = safePage;

      const startIndex = (safePage - 1) * PER_PAGE;
      measure_types.value = allMeasureTypes.value.slice(startIndex, startIndex + PER_PAGE);
    };

    const fetchMeasureTypes = async (page = 1) => {
      try {
        const response = await getMeasureTypes({
          name: searchName.value,
          deleted_status: searchDeletedStatus.value
        });

        const payload = Array.isArray(response.data)
          ? response.data
          : (response.data.measure_types || []);

        allMeasureTypes.value = payload;
        applyPagination(page);
      } catch (error) {
        console.error('測定種別情報データの取得に失敗しました:', error);
      }
    };

    const handleChangePage = (page) => {
      if (page < 1 || page > totalPages.value) return;

      applyPagination(page);

      const q = getSearchQuery();
      q.measure_type_page = page;
      router.replace({ path: '/measure-types', query: q });

      if (tableWrapper.value) {
        tableWrapper.value.scrollTop = 0;
      }
    };

    const handleDeleteClick = (measureTypeId) => {
      paramsIdToDelete.value = measureTypeId;
    };

    const handleRestoreClick = (measureTypeId) => {
      paramsIdToRestore.value = measureTypeId;
    };

    const handleConfirmDelete = async (measureTypeId) => {
      try {
        await deleteMeasureType(measureTypeId);
        store.commit('setFlashMessage', { message: '測定種別情報が削除されました', type: 'success' });
        await fetchMeasureTypes(currentPage.value);
      } catch (error) {
        store.commit('setFlashMessage', { message: '測定種別情報削除に失敗しました', type: 'danger' });
      }
    };

    const handleConfirmRestore = async (measureTypeId) => {
      try {
        await restoreMeasureType(measureTypeId);
        store.commit('setFlashMessage', { message: '測定種別情報が復元されました', type: 'success' });
        await fetchMeasureTypes(currentPage.value);
      } catch (error) {
        store.commit('setFlashMessage', { message: '測定種別情報復元に失敗しました', type: 'danger' });
      }
    };

    const downloadCsv = async () => {
      try {
        const response = await exportMeasureTypesCsv({
          name: searchName.value,
          deleted_status: searchDeletedStatus.value
        });

        const now = new Date();
        const timestamp = `${now.getFullYear()}${(now.getMonth() + 1).toString().padStart(2, '0')}${now.getDate().toString().padStart(2, '0')}_${now.getHours().toString().padStart(2, '0')}${now.getMinutes().toString().padStart(2, '0')}${now.getSeconds().toString().padStart(2, '0')}`;
        const fileName = `m_measure_types_${timestamp}.csv`;

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error('CSVダウンロードエラー', error);
      }
    };

    const handleFileChange = (event) => {
      selectedFile.value = event.target.files[0];
    };

    const closeModal = () => {
      const modalElement = document.getElementById('exampleModal');
      const modal = Modal.getInstance(modalElement) || new Modal(modalElement);
      modal.hide();
    };

    const clearErrorMessages = () => {
      errorMessages.value = {};
      selectedFile.value = null;

      const input = document.getElementById('measureTypeCsvFileInput');
      if (input) {
        input.value = '';
      }
    };

    const submitFile = async () => {
      if (!selectedFile.value) {
        store.commit('setFlashMessage', { message: 'ファイルが選択されていません', type: 'danger' });
        return;
      }

      try {
        await importMeasureTypesCsv(selectedFile.value);
        store.commit('setFlashMessage', { message: 'アップロードに成功しました', type: 'success' });
        closeModal();
        clearErrorMessages();
        await fetchMeasureTypes(currentPage.value);
      } catch (error) {
        if (error.response && error.response.data) {
          errorMessages.value = error.response.data || {};
        }
        console.error('アップロードに失敗しました', error);
        store.commit('setFlashMessage', { message: 'アップロードに失敗しました', type: 'danger' });
      }
    };

    const searchMeasureTypes = async () => {
      await fetchMeasureTypes(1);

      const q = getSearchQuery();
      q.measure_type_page = 1;
      router.push({ path: '/measure-types', query: q });
    };

    const searchResetMeasureTypes = async () => {
      searchName.value = '';
      searchDeletedStatus.value = 'active';
      await fetchMeasureTypes(1);

      const q = getSearchQuery();
      q.measure_type_page = 1;
      router.replace({ path: '/measure-types', query: q });
    };

    onMounted(() => {
      const q = route.query;
      searchName.value = q.measure_type_name || '';
      searchDeletedStatus.value = q.measure_type_deleted_status || 'active';
      currentPage.value = q.measure_type_page ? Number(q.measure_type_page) : 1;
      fetchMeasureTypes(currentPage.value);
    });

    return {
      title,
      isAdmin,
      route,
      measure_types,
      paramsIdToDelete,
      paramsIdToRestore,
      handleDeleteClick,
      handleRestoreClick,
      handleConfirmDelete,
      handleConfirmRestore,
      downloadCsv,
      selectedFile,
      handleFileChange,
      submitFile,
      errorMessages,
      clearErrorMessages,
      searchName,
      searchDeletedStatus,
      getSearchQuery,
      currentPage,
      totalPages,
      handleChangePage,
      searchMeasureTypes,
      searchResetMeasureTypes,
      isSearchExpanded,
      topFixArea,
      tableWrapper,
      paginationContainer
    };
  }
};
</script>

<style scoped>
.list-container {
  padding: 20px 20px 80px 20px;
  margin: auto;
}

.table-wrapper {
  overflow: auto;
  max-height: calc(100vh - 320px);
}

.master-table .btn-cell {
  width: 240px;
  min-width: 240px;
  max-width: 240px;
}

.no-active-measure-type td {
  background-color: #aaa;
}
</style>
